import {SE_METRICS_CONFIG} from '../constants'
import {GLOBAL_KPI_FIELD_TYPE, type GlobalKpiFieldType} from '../constants'

/**
 * Check if data is valid for a specific KPI key
 * @param key - KPI key name
 * @param value - KPI value to validate
 * @returns boolean indicating if data is valid
 */
export const isValidData = (key: string, value: any): boolean => {
  if (value === null || value === undefined || value === '!' || value === 'ERROR') {
    return false
  }

  switch (key) {
    case 'APP_RESPONSE_TIME':
      return value && typeof value === 'object' && value.KEY_ART_OVERALL !== null

    case 'UNIT_TEST_COVERAGE':
      return value && typeof value === 'object' && !isNaN(parseFloat(value.value)) && value.value !== 'NAN'

    case 'TEST_AUTOMATION_TITLE':
      return value.totalTestCasesCount !== 0

    case 'REUSABILITY_TITLE':
      return value.totalStoryPoints !== 0

    case 'DF_AND_CFR':
      return value?.hotfix?.total != null || value?.prod?.total != null

    default:
      if (typeof value === 'object') {
        return Object.values(value).some(val => isValidData(key, val))
      }
      return true
  }
}

/**
 * Check if metrics data is sufficient (meets 70% threshold)
 * @param metricsData - Metrics data object containing automatic and manual KPIs
 * @returns boolean indicating if data is sufficient
 */
export const isMetricsDataSufficient = (metricsData: any): boolean => {
  const automaticKPIs = metricsData?.automatic || {}
  const manualKPIs = metricsData?.manual || {}
  const mergedKPIs = {...manualKPIs, ...automaticKPIs}

  const kpiKeys = Object.keys(mergedKPIs)
  const totalKPIs = kpiKeys.length

  if (totalKPIs === 0) return false

  // Count KPIs with valid data
  const kpisWithData = kpiKeys.filter(key => isValidData(key, mergedKPIs[key])).length
  const percentageWithData = (kpisWithData / totalKPIs) * 100

  // Check if threshold is met
  return percentageWithData >= SE_METRICS_CONFIG.DATA_SUFFICIENCY_THRESHOLD
}

/**
 * Parses a KPI form value into the numeric value sent in the payload.
 * - Blank/untouched fields stay `null` (never coerced to 0).
 * - Stepper fields parse as integers; all others as floats.
 * - Non-numeric input resolves to `null`.
 */
export const parseKpiFieldValue = (value: unknown, type: GlobalKpiFieldType): number | null => {
  if (value === '' || value == null) return null
  const parsed = type === GLOBAL_KPI_FIELD_TYPE.STEPPER ? parseInt(String(value), 10) : parseFloat(String(value))
  return isNaN(parsed) ? null : parsed
}
